import axios from 'axios';
import type { RegisterRequest, LoginRequest, AuthResponse, ApiResponse } from '@/types/auth';
// import { getApiUrl } from '@/config/config';

const API_URL = 'https://aivitahelper.xyz:8081/api/auth';

// 创建axios实例
const instance = axios.create({
  baseURL: API_URL,
  timeout: 10000 // 请求超时时间
});

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // 服务器返回错误状态码
      switch (error.response.status) {
        case 400:
          error.message = error.response.data.message || '请求参数错误';
          break;
        case 401:
          error.message = '未授权，请重新登录';
          // 处理登出逻辑
          localStorage.removeItem('token');
          window.location.href = '/login';
          break;
        case 403:
          error.message = '访问被拒绝，请重新登录';
          // 处理登出逻辑
          localStorage.removeItem('token');
          window.location.href = '/login';
          break;
        case 404:
          error.message = '请求错误，未找到该资源';
          break;
        case 500:
          error.message = '服务器内部错误';
          break;
        default:
          error.message = '网络错误';
      }
    } else if (error.request) {
      // 请求已经发出，但没有收到响应
      error.message = '服务器无响应，请检查网络连接';
    } else {
      // 请求配置出错
      error.message = '请求配置出错';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // 发送注册验证码 (x-www-form-urlencoded)
  async sendRegisterCode(email: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    params.append('email', email);
    const response = await instance.post('/send-register-code', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  },

  // 验证注册验证码 (x-www-form-urlencoded)
  async verifyRegisterCode(email: string, code: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    params.append('email', email);
    params.append('code', code);
    const response = await instance.post('/verify-register-code', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  },

  // 用户注册 (json)
  async register(data: RegisterRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await instance.post('/register', JSON.stringify(data), {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 5000 // 设置更短的超时时间以便快速发现问题
    });
    return response.data;
  },

  // 用户登录 (json)
  async login(data: LoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await instance.post('/login', JSON.stringify(data), {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 5000
    });
    // 登录成功后保存token
    if (response.data.data?.token) {
      localStorage.setItem('token', response.data.data.token);
    }
    return response.data;
  },

  // 发送密码重置邮件 (x-www-form-urlencoded)
  async forgotPassword(email: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    params.append('email', email);
    const response = await instance.post('/forgot-password', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  },

  // 重置密码 (x-www-form-urlencoded)
  async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    params.append('token', token);
    params.append('newPassword', newPassword);
    const response = await instance.post('/reset-password', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  }
};